import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, Search, ArrowLeft, Lock,
  Crown, ChevronRight, Check, Clock, User, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const { user: currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [messageLimit, setMessageLimit] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      toast.error('Faça login para acessar mensagens');
      navigate('/connect');
      return;
    }
    
    if (currentUser?.id) {
      fetchUserProfile(currentUser.id);
    }
  }, [currentUser, isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (userProfile?.profile_id) {
      fetchConversations();
      fetchMessageLimit();
      
      if (partnerId) {
        fetchConversation(partnerId);
      }
    }
  }, [userProfile, partnerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/connect/profiles/user/${userId}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      } else {
        toast.error('Crie seu perfil primeiro');
        navigate('/connect/create-profile');
      }
    } catch (error) {
      // Profile check failed - navigate to create profile
      navigate('/connect/create-profile');
    }
  };

  const fetchMessageLimit = async () => {
    if (!userProfile?.profile_id) return;
    
    try {
      const response = await axios.get(`${API}/connect/messages/limit?profile_id=${userProfile.profile_id}`);
      setMessageLimit(response.data);
    } catch (error) {
      console.error('Erro ao carregar limite de mensagens:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/connect/conversations?profile_id=${userProfile.profile_id}`);
      setConversations(response.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (id) => {
    try {
      const response = await axios.get(`${API}/connect/conversations/${id}?profile_id=${userProfile.profile_id}`);
      setSelectedConversation(response.data.partner);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Erro ao carregar conversa');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    if (!messageLimit?.can_send) {
      if (messageLimit?.limit === 0) {
        toast.error('Assine um plano premium para enviar mensagens');
      } else {
        toast.error(`Limite de mensagens atingido (${messageLimit?.limit}/mês). Faça upgrade!`);
      }
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(`${API}/connect/messages?sender_id=${userProfile.profile_id}`, {
        recipient_id: selectedConversation.id,
        content: newMessage
      });

      // Add message to local state
      setMessages([...messages, {
        id: Date.now().toString(),
        content: newMessage,
        is_sent: true,
        created_at: new Date().toISOString()
      }]);

      setNewMessage('');

      // Update remaining count
      if (!response.data.unlimited) {
        setMessageLimit(prev => ({
          ...prev,
          remaining: response.data.remaining_messages,
          can_send: response.data.remaining_messages > 0
        }));
      }

      // Refresh conversations
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Agora';
    if (hours < 24) return formatTime(dateStr);
    if (hours < 48) return 'Ontem';
    return date.toLocaleDateString('pt-BR');
  };

  const getLimitBadge = () => {
    if (!messageLimit) return null;
    
    if (messageLimit.unlimited) {
      return (
        <Badge className="bg-green-600/20 text-green-400">
          <Crown className="w-3 h-3 mr-1" />
          Ilimitado
        </Badge>
      );
    }
    
    if (messageLimit.limit === 0) {
      return (
        <Badge className="bg-red-600/20 text-red-400">
          <Lock className="w-3 h-3 mr-1" />
          Premium necessário
        </Badge>
      );
    }
    
    const percentage = (messageLimit.remaining / messageLimit.limit) * 100;
    const color = percentage > 50 ? 'green' : percentage > 20 ? 'yellow' : 'red';
    
    return (
      <Badge className={`bg-${color}-600/20 text-${color}-400`}>
        {messageLimit.remaining}/{messageLimit.limit} mensagens restantes
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <section className="pt-24 pb-8">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/connect')}
                  className="text-gray-400 hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Voltar
                </Button>
                <h1 className="text-2xl font-bold text-white">Mensagens</h1>
              </div>
              {getLimitBadge()}
            </div>

            {/* Premium Warning */}
            {messageLimit && messageLimit.limit === 0 && (
              <Card className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-500/30 mb-6">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-8 h-8 text-purple-400" />
                    <div>
                      <h3 className="font-semibold text-white">Mensagens são um recurso premium</h3>
                      <p className="text-sm text-gray-400">Assine um plano para conversar com outros profissionais</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/connect#plans')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Ver Planos
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Low limit warning */}
            {messageLimit && messageLimit.remaining <= 2 && messageLimit.remaining > 0 && (
              <Card className="bg-yellow-900/20 border-yellow-500/30 mb-6">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                    <p className="text-yellow-300">
                      Apenas <strong>{messageLimit.remaining}</strong> mensagem(ns) restante(s) este mês
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/connect#plans')}
                    className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                  >
                    Fazer Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Messages Container */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-280px)]">
              {/* Conversations List */}
              <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <CardHeader className="p-4 border-b border-zinc-800">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      placeholder="Buscar conversas..."
                      className="pl-9 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-y-auto h-[calc(100%-80px)]">
                  {loading ? (
                    <div className="p-4 text-center text-gray-400">
                      Carregando...
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm">Nenhuma conversa ainda</p>
                      <p className="text-gray-500 text-xs mt-1">Conecte-se com profissionais para iniciar</p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <div
                        key={conv.partner_id}
                        onClick={() => {
                          navigate(`/connect/messages/${conv.partner_id}`);
                          fetchConversation(conv.partner_id);
                        }}
                        className={`p-4 cursor-pointer hover:bg-zinc-800 transition-colors border-b border-zinc-800/50 ${
                          selectedConversation?.id === conv.partner_id ? 'bg-zinc-800' : ''
                        }`}
                        data-testid={`conversation-${conv.partner_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                              {conv.partner_photo ? (
                                <img src={conv.partner_photo} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                conv.partner_name?.charAt(0)?.toUpperCase()
                              )}
                            </div>
                            {conv.unread_count > 0 && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full text-xs text-white flex items-center justify-center">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-white truncate">{conv.partner_name}</h4>
                              <span className="text-xs text-gray-500">{formatDate(conv.last_message_time)}</span>
                            </div>
                            <p className="text-sm text-gray-500 truncate">{conv.partner_headline}</p>
                            <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-white font-medium' : 'text-gray-400'}`}>
                              {conv.is_sent && '✓ '}
                              {conv.last_message}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Chat Area */}
              <Card className="bg-zinc-900 border-zinc-800 md:col-span-2 flex flex-col">
                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <CardHeader className="p-4 border-b border-zinc-800 flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                          {selectedConversation.photo ? (
                            <img src={selectedConversation.photo} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            selectedConversation.name?.charAt(0)?.toUpperCase()
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{selectedConversation.name}</h3>
                          <p className="text-sm text-gray-500">{selectedConversation.headline}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          className="ml-auto text-gray-400"
                          onClick={() => navigate(`/connect/profile/${selectedConversation.id}`)}
                        >
                          Ver Perfil
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardHeader>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_sent ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              msg.is_sent
                                ? 'bg-blue-600 text-white rounded-br-md'
                                : 'bg-zinc-800 text-white rounded-bl-md'
                            }`}
                          >
                            <p>{msg.content}</p>
                            <div className={`flex items-center gap-1 mt-1 text-xs ${msg.is_sent ? 'text-blue-200' : 'text-gray-500'}`}>
                              <span>{formatTime(msg.created_at)}</span>
                              {msg.is_sent && msg.is_read && <Check className="w-3 h-3" />}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-zinc-800 flex-shrink-0">
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={messageLimit?.can_send ? "Digite sua mensagem..." : "Assine um plano para enviar mensagens"}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          disabled={!messageLimit?.can_send}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          data-testid="message-input"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={!messageLimit?.can_send || isSending || !newMessage.trim()}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="send-message-btn"
                        >
                          <Send className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">Suas mensagens</h3>
                      <p className="text-gray-400">Selecione uma conversa para começar</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MessagesPage;
